import { DOC_STATUS, DOC_TYPE, PUBLISHED, MODALITY, HEALTH, LIFECYCLE_STATUS, SESSION_ACTIVE, RETRIEVAL, CACHE, INTENT, FALLBACK, AUDIT_ROLE } from '../utils/chipContract';

function Chip({ variant, emoji }) {
  return (
    <span className={`ai-status ${variant} ai-status--emoji ai-status--icon-lg`}>
      {emoji}
    </span>
  );
}

// Section card: emoji (icon size) → status name (12px) → explanation (12px).
// Every card has the same width (grid) and the same height; the uniform
// minimum is slightly reduced and line spacing tightened (owner: three grid
// rows fit the viewport without scrolling), short panels keep empty space.
function Section({ title, rows }) {
  return (
    <div className="rounded-ai border border-ai-border bg-ai-surface p-4 min-h-[290px]">
      <h3 className="ai-section__title">{title}</h3>
      <div className="mt-2 space-y-2">
        {rows.map((row, idx) => (
          <div key={idx} className="flex items-baseline gap-2">
            <span className="flex-shrink-0">
              <Chip variant={row.variant} emoji={row.chip.emoji} />
            </span>
            <span className="w-28 flex-shrink-0 text-xs font-medium text-ai-text">
              {row.chip.label}
            </span>
            <span className="min-w-0 flex-1 text-xs text-ai-text-secondary">{row.note}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const MUTED = 'ai-status--muted';
const ERR = 'ai-status--error';
const OKS = 'ai-status--signal--success';
const WARN = 'ai-status--signal--warning';
const INF = 'ai-status--signal--info';
const PRIM = 'ai-status--signal--primary';

const DOC_STATUS_AR = [
  { chip: DOC_STATUS.draft, variant: MUTED, note: 'документ ещё не обрабатывался' },
  { chip: DOC_STATUS.pending, variant: MUTED, note: 'обработка запланирована' },
  { chip: DOC_STATUS.processing, variant: MUTED, note: 'идёт обработка' },
  { chip: DOC_STATUS.indexed, variant: MUTED, note: 'версия проиндексирована' },
  { chip: DOC_STATUS.error, variant: ERR, note: 'ошибка обработки' },
  { chip: DOC_STATUS.archived, variant: MUTED, note: 'удалён (мягкая архивация)' },
  { chip: PUBLISHED, variant: OKS, note: 'входит в публикуемый контур для студентов' },
];

const DOC_TYPE_AR = [
  { chip: DOC_TYPE.lecture, variant: MUTED, note: 'лекционный материал' },
  { chip: DOC_TYPE.methodical, variant: MUTED, note: 'методические материалы' },
  { chip: DOC_TYPE.faq, variant: MUTED, note: 'частые вопросы' },
  { chip: DOC_TYPE.instruction, variant: MUTED, note: 'инструкции' },
  { chip: DOC_TYPE.glossary, variant: MUTED, note: 'термины курса' },
  { chip: DOC_TYPE.example, variant: MUTED, note: 'примеры заданий' },
  { chip: DOC_TYPE.external, variant: MUTED, note: 'внешние материалы' },
];

const INTENT_AR = [
  { chip: INTENT.study, variant: MUTED, note: 'учёба: материалы и вопросы по курсу' },
  { chip: INTENT.organizational, variant: MUTED, note: 'организационное: расписание, LMS' },
  { chip: INTENT.mixed, variant: INF, note: 'учебный + организационный в одном вопросе' },
  { chip: INTENT.progress, variant: MUTED, note: 'успеваемость, баллы, рейтинги' },
  { chip: INTENT.deadline, variant: MUTED, note: 'сроки и дедлайны заданий' },
  { chip: INTENT.out_of_scope, variant: WARN, note: 'вопрос вне курса — не распределён' },
  { chip: INTENT.error, variant: ERR, note: 'интент не определён из-за ошибки' },
];

const MODALITY_AR = [
  { chip: MODALITY.rag, variant: PRIM, note: 'ответ из базы знаний' },
  { chip: MODALITY.text, variant: OKS, note: 'текстовый ответ моделью' },
  { chip: MODALITY.lms, variant: WARN, note: 'материалы курса из LMS' },
  { chip: MODALITY.mixed, variant: INF, note: 'комбинированный ответ' },
  { chip: MODALITY.log, variant: MUTED, note: 'системное событие' },
];

const LIFECYCLE_AR = [
  { chip: LIFECYCLE_STATUS.success, variant: OKS, note: 'событие завершилось без ошибок (зелёная галка)' },
  { chip: LIFECYCLE_STATUS.error, variant: ERR, note: 'событие завершилось ошибкой' },
  { chip: LIFECYCLE_STATUS.pending, variant: MUTED, note: 'событие ещё выполняется' },
];

const MISC_AR = [
  { chip: CACHE, variant: MUTED, note: 'ответ взят из кэша' },
  { chip: RETRIEVAL.hit, variant: OKS, note: 'чанк попадает в порог близости' },
  { chip: RETRIEVAL.miss, variant: WARN, note: 'чанк вне порога близости' },
  { chip: SESSION_ACTIVE.active, variant: OKS, note: 'активная сессия' },
  { chip: SESSION_ACTIVE.inactive, variant: MUTED, note: 'сессия завершена' },
  { chip: FALLBACK, variant: WARN, note: 'ответ от резервного механизма бэкенда' },
];

// Audit journal roles — same emoji alphabet as the RF reference console.
const ROLES_AR = [
  { chip: AUDIT_ROLE.admin, variant: MUTED, note: 'действия в админ-консоли (контент, конфигурация, ретривер)' },
  { chip: AUDIT_ROLE.student, variant: MUTED, note: 'пользователь чата: вопросы и оценки ответов' },
  { chip: AUDIT_ROLE.demo, variant: MUTED, note: 'демо-режим админ-консоли (read-only)' },
  { chip: AUDIT_ROLE.system, variant: MUTED, note: 'служебные события бэкенда (очистка по retention)' },
];

// Three plus three: equal-width panels across the whole working area,
// equal heights (sized for the seven-row panels, empty space allowed).
export default function Legend() {
  return (
    <div className="ai-config-page">
      <h2 className="text-base font-semibold text-ai-text">Обозначения</h2>
      <p className="text-xs text-ai-text-secondary">
        Единый чип-контракт консоли: значок определяет смысл статуса и не меняется от экрана
        к экрану. В фильтрах значок стоит рядом с тем же значением.
      </p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <Section title="Статусы документов" rows={DOC_STATUS_AR} />
        <Section title="Типы документов" rows={DOC_TYPE_AR} />
        <Section title="Интенты диалога" rows={INTENT_AR} />
        <Section title="Модальность диалога" rows={MODALITY_AR} />
        <Section title="Жизненный цикл события" rows={LIFECYCLE_AR} />
        <Section title="Прочие пометки" rows={MISC_AR} />
        <Section title="Роли (журнал аудита)" rows={ROLES_AR} />
      </div>
    </div>
  );
}